import { PantallaCargando } from '@/components/Cargando';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ChevronRight, Filter, Mail, MessageCircle, Plus, Send,
} from 'lucide-react';

/**
 * S82.1 — Pantalla "Envíos" al estilo Bewe / LeadCEM.
 * No inventa motor nuevo: lee delivery runs + schedules + automatizaciones
 * y las presenta con el lenguaje que el dueño ya entiende.
 *
 * channel: 'email' | 'whatsapp'
 */
const DONE_STATUSES = new Set([
  'mass_completed', 'mass_partial', 'mass_failed', 'test_success', 'test_failed',
]);
const PROG_SCHEDULE = new Set([
  'draft', 'preflight_required', 'preflight_passed', 'ready_for_delivery_future',
]);
const PROG_RUN = new Set([
  'draft', 'planned', 'dry_run_completed', 'test_ready', 'test_running',
  'mass_ready', 'mass_running',
]);

const RUN_LABEL = {
  mass_completed: 'Enviado',
  mass_partial: 'Parcial',
  mass_failed: 'Falló',
  test_success: 'Prueba OK',
  test_failed: 'Prueba falló',
  mass_running: 'Enviando…',
  mass_ready: 'Listo',
  draft: 'Borrador',
  planned: 'Planificado',
};

function sameDay(iso, day) {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === day.getFullYear()
    && d.getMonth() === day.getMonth()
    && d.getDate() === day.getDate();
}

function formatDay(d) {
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function isChannel(rowChannel, wanted) {
  const c = (rowChannel || '').toLowerCase();
  if (wanted === 'email') return c.includes('email') || c === 'generic' || !c;
  if (wanted === 'whatsapp') return c.includes('whatsapp');
  return true;
}

export default function MarketingEnvios({ channel = 'email' }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('realizados');
  const [day, setDay] = useState(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  });
  const [vistaHoy, setVistaHoy] = useState(true);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [quota, setQuota] = useState(null);

  const createPath = channel === 'whatsapp'
    ? '/tenant/whatsapp/envios'
    : '/tenant/email/nuevo';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, sRes, aRes, limRes] = await Promise.all([
        api.get('/tenant/campaign-delivery/runs?page_size=50').catch(() => ({ data: { items: [] } })),
        api.get('/tenant/campaign-schedules?page_size=50').catch(() => ({ data: { items: [] } })),
        api.get('/tenant/marketing/automations').catch(() => ({ data: [] })),
        api.get('/tenant/marketing/limits').catch(() => ({ data: {} })),
      ]);
      setRuns(rRes.data?.items || []);
      setSchedules(sRes.data?.items || []);
      const autos = Array.isArray(aRes.data) ? aRes.data : (aRes.data?.items || aRes.data?.flows || []);
      setAutomations(autos);
      setQuota(limRes.data || {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const channelRuns = useMemo(
    () => runs.filter(r => isChannel(r.channel, channel)),
    [runs, channel],
  );
  const channelSchedules = useMemo(
    () => schedules.filter(s => isChannel(s.planned_channel, channel)),
    [schedules, channel],
  );

  const realizados = useMemo(() => {
    let list = channelRuns.filter(r => DONE_STATUSES.has(r.status));
    if (vistaHoy) list = list.filter(r => sameDay(r.finished_at || r.updated_at || r.created_at, day));
    return list;
  }, [channelRuns, vistaHoy, day]);

  const programados = useMemo(() => {
    const fromSched = channelSchedules.filter(s => PROG_SCHEDULE.has(s.status) && !s.archived_at);
    const fromRuns = channelRuns.filter(r => PROG_RUN.has(r.status));
    let list = [
      ...fromSched.map(s => ({ kind: 'schedule', ...s })),
      ...fromRuns.map(r => ({ kind: 'run', ...r })),
    ];
    if (vistaHoy) {
      list = list.filter(x => sameDay(x.planned_start_at || x.updated_at || x.created_at, day));
    }
    return list;
  }, [channelSchedules, channelRuns, vistaHoy, day]);

  const automaticos = useMemo(() => {
    // Automatizaciones activas del canal (cadencias 1:1, no mailing masivo)
    return (automations || []).filter(a => {
      const st = a.status || a.state;
      if (st && st !== 'active' && st !== 'enabled') return false;
      const ch = JSON.stringify(a).toLowerCase();
      if (channel === 'email') return ch.includes('email') || ch.includes('correo');
      if (channel === 'whatsapp') return ch.includes('whatsapp') || ch.includes('template');
      return true;
    });
  }, [automations, channel]);

  const shiftDay = (delta) => {
    setVistaHoy(true);
    setDay(prev => {
      const n = new Date(prev);
      n.setDate(n.getDate() + delta);
      return n;
    });
  };

  const goToday = () => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    setDay(d);
    setVistaHoy(true);
  };

  const emailsDisponibles = quota?.platform_email_monthly_quota
    ?? quota?.max_mass_sends_daily
    ?? null;

  const Icon = channel === 'whatsapp' ? MessageCircle : Mail;
  const accent = channel === 'whatsapp' ? '#128C7E' : '#F72585';

  if (loading) return <PantallaCargando />;

  const rows = tab === 'realizados' ? realizados
    : tab === 'programados' ? programados
      : automaticos;

  return (
    <div className="space-y-5" data-testid={`marketing-envios-${channel}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: accent }} /> Envíos
          </h2>
          {channel === 'email' && emailsDisponibles != null && (
            <p className="text-sm mt-1 text-[#64748B]">
              Email disponibles para campañas:{' '}
              <span className="font-semibold text-emerald-600" data-testid="envios-quota">{emailsDisponibles}</span>
            </p>
          )}
          {channel === 'whatsapp' && (
            <p className="text-sm mt-1 text-[#64748B]">
              Campañas con plantillas Meta aprobadas · no es el chat de Conversaciones
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {channel === 'email' && (
            <Button variant="outline" className="text-xs rounded-full border-[#E2E8F0]"
              onClick={() => navigate('/tenant/marketing/email?tab=dominio')}>
              Mi dominio
            </Button>
          )}
          <Button
            onClick={() => navigate(createPath)}
            className="text-xs rounded-full text-white"
            style={{ background: accent }}
            data-testid="envios-crear"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Crear envío
          </Button>
        </div>
      </div>

      {/* Filtro fecha — Bewe: Vista hoy + flechas */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={goToday}
          className={`px-3 py-1.5 text-xs rounded-full border ${vistaHoy ? 'bg-white border-[#E2E8F0] text-[#0F172A] shadow-sm' : 'border-transparent text-[#64748B]'}`}>
          ▾ Vista hoy
        </button>
        <button type="button" onClick={() => shiftDay(-1)} className="w-8 h-8 rounded-full border border-[#E2E8F0] flex items-center justify-center text-[#64748B] hover:bg-[#F8FAFC]" aria-label="Día anterior">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => shiftDay(1)} className="w-8 h-8 rounded-full border border-[#E2E8F0] flex items-center justify-center text-[#64748B] hover:bg-[#F8FAFC]" aria-label="Día siguiente">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="text-sm text-[#334155] capitalize">{formatDay(day)}</span>
        <button type="button" onClick={() => setVistaHoy(v => !v)}
          className="ml-1 text-xs font-medium flex items-center gap-1"
          style={{ color: accent }}
          data-testid="envios-filtro-periodo">
          <Filter className="w-3.5 h-3.5" /> {vistaHoy ? 'Filtrar · solo hoy' : 'Filtrar · todo'}
        </button>
      </div>

      {/* Tabs Bewe */}
      <div className="flex gap-6 border-b border-[#E2E8F0]" data-testid="envios-tabs">
        {[
          ['realizados', 'Realizados'],
          ['programados', 'Programados'],
          ['automaticos', 'Automáticos'],
        ].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === k ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#94A3B8] hover:text-[#64748B]'
            }`}
            data-testid={`envios-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'automaticos' && (
        <p className="text-xs text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2">
          Los <strong>automáticos</strong> son cadencias 1:1 (bienvenida, recordatorio, NPS).
          El mailing masivo de una lista vive en <strong>Realizados</strong> / <strong>Programados</strong>.
        </p>
      )}

      {/* Lista o empty Bewe */}
      {rows.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-[#E2E8F0] py-10 px-4 text-center text-sm text-[#94A3B8]"
          data-testid="envios-empty"
        >
          No hay registros en este período
          <div className="mt-4">
            <Button onClick={() => navigate(createPath)} variant="outline" className="text-xs rounded-full">
              <Send className="w-3.5 h-3.5 mr-1" /> Crear el primer envío
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden divide-y divide-[#F1F5F9]" data-testid="envios-list">
          {tab !== 'automaticos' && rows.map(item => (
            <EnvioRow
              key={`${item.kind || 'run'}-${item.id}`}
              item={item}
              channel={channel}
              onOpen={() => {
                if (item.kind === 'schedule') navigate('/tenant/marketing/schedules');
                else navigate('/tenant/marketing/delivery');
              }}
            />
          ))}
          {tab === 'automaticos' && rows.map(a => (
            <button
              key={a.id || a.key || a.name}
              type="button"
              onClick={() => navigate('/tenant/marketing/automations')}
              className="w-full text-left px-4 py-3 hover:bg-[#F8FAFC] flex items-center justify-between gap-3"
            >
              <div>
                <p className="text-sm font-medium text-[#0F172A]">{a.name || a.title || 'Automatización'}</p>
                <p className="text-[11px] text-[#94A3B8]">{a.status || a.state || 'activa'}</p>
              </div>
              <span className="text-[11px] text-[#64748B]">Ver →</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EnvioRow({ item, channel, onOpen }) {
  const name = item.name || item.run_key || 'Envío';
  const status = item.status;
  const label = RUN_LABEL[status] || status || 'Programado';
  const when = item.finished_at || item.planned_start_at || item.updated_at || item.created_at;
  const sent = item.sent_recipients;
  const total = item.total_recipients || item.eligible_recipients;

  return (
    <button type="button" onClick={onOpen}
      className="w-full text-left px-4 py-3 hover:bg-[#F8FAFC] flex items-center justify-between gap-3"
      data-testid={`envio-row-${item.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-[#0F172A] truncate">{name}</p>
        <p className="text-[11px] text-[#94A3B8]">
          {channel === 'whatsapp' ? 'WhatsApp' : 'Email'}
          {when ? ` · ${new Date(when).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
          {sent != null && total != null ? ` · ${sent}/${total} enviados` : ''}
        </p>
      </div>
      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${
        status === 'mass_completed' || status === 'test_success' ? 'bg-emerald-50 text-emerald-700'
          : status === 'mass_failed' || status === 'test_failed' ? 'bg-red-50 text-red-600'
            : 'bg-[#F1F5F9] text-[#64748B]'
      }`}>{label}</span>
    </button>
  );
}
